import React, { useEffect, useState } from "react";
import { CalendarCheck, RefreshCw, Trash2, Search, Video } from "lucide-react";
import { adminBookings, adminPatchBooking, adminDeleteBooking } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { useToast } from "../../hooks/use-toast";

const STATUSES = ["pending", "confirmed", "cancelled"];

export default function DashboardBookings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminBookings();
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (b, status) => {
    try {
      const { data } = await adminPatchBooking(b.id, { status });
      setRows((prev) => prev.map((r) => (r.id === b.id ? data : r)));
    } catch (e) {
      toast({ title: "Failed to update booking" });
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete booking for ${b.name} on ${b.date}?`)) return;
    try {
      await adminDeleteBooking(b.id);
      setRows((prev) => prev.filter((r) => r.id !== b.id));
    } catch (e) {
      toast({ title: "Failed to delete" });
    }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.name, r.email, r.company, r.topic].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="kicker">Schedule</div>
          <h1 className="mt-2 font-display text-3xl font-bold text-white">Bookings</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bookings..." className="pl-9 h-10 w-72 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-500" />
          </div>
          <button onClick={load} className="h-10 px-3 rounded-lg border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-white flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest border transition ${
              statusFilter === s
                ? "bg-gradient-to-r from-sky-500 to-cyan-400 text-white border-transparent"
                : "border-white/10 text-slate-300 hover:border-cyan-400/40"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarCheck className="w-10 h-10 text-cyan-400/40 mx-auto mb-3" />
            <div className="text-sm text-slate-500">No bookings found.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400">
                  <Th>Attendee</Th>
                  <Th>Date & Time</Th>
                  <Th>Topic</Th>
                  <Th>Status</Th>
                  <Th className="text-right pr-6">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.id} className="border-b border-white/5 last:border-none hover:bg-white/[0.02]">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-white">{b.name}</div>
                      <div className="text-xs text-slate-400">{b.email}</div>
                      {b.company && <div className="text-[11px] text-slate-500">{b.company}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white">{b.date}</div>
                      <div className="text-xs text-cyan-300 flex items-center gap-1.5"><Video className="w-3 h-3" /> {b.time}</div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-slate-300 line-clamp-2">{b.topic || <span className="text-slate-600">—</span>}</div>
                      {b.phone && <div className="text-[11px] text-slate-500 mt-1">{b.phone}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={b.status}
                        onChange={(e) => setStatus(b, e.target.value)}
                        className="bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1 text-xs text-slate-200 focus:border-cyan-400/40 focus:outline-none"
                      >
                        {STATUSES.map((s) => (<option key={s} value={s} className="bg-[#0a1030]">{s}</option>))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => remove(b)} className="h-8 px-2.5 rounded-lg border border-red-400/20 hover:border-red-400/50 text-red-300 text-xs inline-flex items-center gap-1.5">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-6 py-3 font-semibold ${className}`}>{children}</th>;
}
